import { useMemo, useRef, useState } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import {
  Divider,
  Menu,
  PaperProvider,
  Text,
  useTheme,
} from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  MelddTaskTargetType,
  useGetTarget,
} from '../../screens/tasks/task.types'
import colors from '../../constants/colors'
import {
  IconAssignTask,
  IconCompleted,
  IconCreatedTask,
  IconM,
} from '../../utils/meldd-icons'
import {
  CreateTaskModal,
  CreateTaskModalProps,
} from '../../screens/tasks/components/create-task.modal'
import { useAuthContext } from '../../contexts/auth-context'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../navigation/types'
import { useGetNewEventsCountQuery } from '../../generated/graphql'

type Props = {
  targetId: string
  targetType: MelddTaskTargetType
  preview: string
}
/**
 * M Button is a fix button on the bottom of the screen that opens a menu with different actions
 * Different pages may have different actions
 */

const MButtonTrigger = ({
  onPress,
  visible,
}: {
  onPress: (flag: boolean) => void
  visible: boolean
}) => {
  const theme = useTheme()
  const styles = useStyle()
  return (
    <Pressable
      onPress={() => onPress(!visible)}
      style={({ pressed }) => [
        {
          opacity: pressed ? 0.5 : 1,
        },
        styles.button,
      ]}
    >
      {!visible ? (
        <IconM color={theme.colors.primary} />
      ) : (
        <Text style={styles.text}>{'X'}</Text>
      )}
    </Pressable>
  )
}

type MButtonEventIndicatorProps = {
  nodeId?: string
}

export const MButtonEventIndicator = ({
  nodeId,
}: MButtonEventIndicatorProps) => {
  const styles = useStyle()
  const navigator = useNavigation<AppNavigation>()
  const { authAction } = useAuthContext()

  const { data } = useGetNewEventsCountQuery({
    variables: { input: { nodeId } },
    skip: !nodeId,
  })

  const count = data?.getNewEventsCount ?? 0

  if (count <= 0) {
    return null
  }

  const displayCount = count > 99 ? '99+' : `${count}`

  return (
    <Pressable
      style={styles.indicatorContainer}
      onPress={() => {
        authAction(navigator, () => {
          navigator.navigate('MyCollaborators', {
            tab: 'updates',
            ...(nodeId ? { nodeId } : {}),
          })
        })
      }}
    >
      <View style={styles.indicatorBubble}>
        <Text style={styles.indicatorText}>{displayCount}</Text>
      </View>
    </Pressable>
  )
}

const MButton = (props: Props) => {
  const { t } = useTranslation('menu')
  const createTaskModalRef = useRef<CreateTaskModalProps>(null)
  const [visible, setVisible] = useState(false)
  const styles = useStyle()
  const { authAction } = useAuthContext()
  const navigator = useNavigation<AppNavigation>()
  const theme = useTheme()

  const { data: target } = useGetTarget({
    targetType: props.targetType,
    targetId: props.targetId,
  })

  const getNodeId = (): string | undefined => {
    if (props.targetType === MelddTaskTargetType.NODE_TASK) {
      return props.targetId
    }
    if (!target) {
      return undefined
    }
    if (props.targetType === MelddTaskTargetType.CLUSTER_TASK) {
      const cluster = target as { nodeId?: string }
      return cluster.nodeId
    }
    if (props.targetType === MelddTaskTargetType.ARTICLE_TASK) {
      const article = target as { cluster?: { nodeId?: string } }
      return article.cluster?.nodeId
    }
    if (props.targetType === MelddTaskTargetType.CONTRIBUTION_TASK) {
      const contribution = target as {
        article?: { cluster?: { nodeId?: string } }
      }
      return contribution.article?.cluster?.nodeId
    }
    return undefined
  }

  const nodeId = getNodeId()
  const { data: eventsData } = useGetNewEventsCountQuery({
    variables: { input: { nodeId } },
    skip: !nodeId,
  })
  const eventsCount = eventsData?.getNewEventsCount ?? 0

  return (
    <>
      <CreateTaskModal ref={createTaskModalRef} />
      <View style={[styles.container, visible && styles.containerMenuOpen]}>
        <PaperProvider theme={theme}>
          <Menu
            visible={visible}
            onDismiss={() => setVisible(false)}
            anchor={
              <MButtonTrigger
                onPress={(visible) => setVisible(visible)}
                visible={visible}
              />
            }
            style={styles.menu}
          >
            {/*
            TODO: Uncomment when we have the assigned task functionality
            <Menu.Item
              leadingIcon={IconAssignedTask}
              onPress={() => {}}
              title={t('assigned-task')}
              onPress={() => {
                authAction(navigator, () => {
                  navigator.navigate('MyTasks', { tab: 'assigned' })
                })
              }}
            /> */}
            <Menu.Item
              leadingIcon={IconCompleted}
              title={t('completed-tasks')}
              onPress={() => {
                authAction(navigator, () => {
                  navigator.navigate('MyTasks', { tab: 'completed' })
                })
              }}
            />
            <Menu.Item
              leadingIcon={() => <IconCreatedTask />}
              onPress={() => {
                authAction(navigator, () => {
                  navigator.navigate('MyTasks', { tab: 'pending' })
                })
              }}
              title={t('pending-tasks')}
            />
            <Divider />
            <Menu.Item
              leadingIcon="account-group-outline"
              onPress={() => {
                authAction(navigator, () => {
                  const nodeId = getNodeId()
                  navigator.navigate('MyCollaborators', {
                    tab: 'updates',
                    ...(nodeId ? { nodeId } : {}),
                  })
                  setVisible(false)
                })
              }}
              title={`${t('collaborators')}${
                eventsCount > 0
                  ? ` (${eventsCount > 99 ? '99+' : eventsCount})`
                  : ''
              }`}
            />
            <Menu.Item
              leadingIcon={() => <IconAssignTask />}
              onPress={() => {
                authAction(navigator, () => {
                  createTaskModalRef.current?.open(
                    props.targetId,
                    props.targetType,
                    props.preview
                  )
                  setVisible(false)
                })
              }}
              title={t('create-task')}
            />
          </Menu>
        </PaperProvider>
      </View>
    </>
  )
}

export default MButton

const useStyle = () => {
  const theme = useTheme()
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          position: 'absolute',
          right: 32,
          bottom: 32,
          zIndex: 99,
        },
        containerMenuOpen: {
          zIndex: 9999,
        },
        indicatorContainer: {
          position: 'absolute',
          right: 26,
          bottom: 72,
          zIndex: 100,
        },
        indicatorBubble: {
          minWidth: 20,
          height: 20,
          borderRadius: 10,
          backgroundColor: theme.colors.primary,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: 4,
        },
        indicatorText: {
          color: 'white',
          fontSize: 11,
          fontWeight: 'bold',
        },
        menu: {
          position: 'absolute',
          top: -170,
          left: -122,
        },
        button: {
          backgroundColor: colors.screen,
          borderRadius: 12,
          width: 56,
          height: 56,
          justifyContent: 'center',
          alignItems: 'center',
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 2,
          },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5, // for Android
        },
        text: {
          fontSize: 24,
          fontWeight: 'bold',
          color: theme.colors.primary,
        },
      }),
    [theme]
  )
}
